import { Link } from "react-router-dom";
import { useState } from "react";
import SEOHead from "@/components/feature/SEOHead";
import ArticleCard from "@/components/base/ArticleCard";
import { articles, categories } from "@/mocks/articles";

const featuredArticles = articles.filter((a) => a.featured).slice(0, 3);
const latestArticles = articles.filter((a) => !a.featured).slice(0, 6);

const faqs = [
  {
    question: "韩国女主播为什么能吸引全球观众？",
    answer: "韩国女主播通过视觉优先的传播策略、韩流文化的全球红利以及粉丝字幕翻译社群的助力，成功跨越了语言和文化壁垒。她们精致的画面呈现、丰富的互动技巧和成熟的粉丝运营体系，为全球观众提供了独特的内容消费体验。",
  },
  {
    question: "韩国女主播主要在哪些平台直播？",
    answer: "韩国女主播主要在AfreecaTV（韩国本土最大直播平台）、YouTube直播和Twitch等平台进行直播。其中AfreecaTV以其成熟的星气球打赏体系和BJ文化占据核心地位，YouTube则凭借庞大的用户基数和算法推荐能力日益受到青睐。",
  },
  {
    question: "韩国女主播如何实现变现？",
    answer: "韩国女主播的变现模式包括：观众打赏（星气球等虚拟礼物）、品牌合作与广告代言、电商带货与自有品牌、付费订阅与会员体系等多种渠道。头部主播还会通过线下活动、综艺节目录制等方式拓展收入来源。",
  },
  {
    question: "如何找到值得关注的韩国女主播？",
    answer: "可以从YouTube平台开始，使用分类浏览和算法推荐功能发现优质内容。明确自己感兴趣的内容类型（游戏、聊天、ASMR、美妆、吃播等），关注综合评分较高的主播。我们网站也定期更新韩国女主播推荐榜单供参考。",
  },
  {
    question: "韩国女主播的直播有哪些独特风格？",
    answer: "韩国女主播的直播风格以撒娇文化、精致的场景布置和高效的互动体系为特色。她们擅长运用语调变化、肢体语言、微表情管理等技巧营造亲密感，同时通过精心设计的灯光、背景和服装构建视觉享受，形成了独特的韩式直播美学。",
  },
  {
    question: "韩国女主播如何运营粉丝社群？",
    answer: "韩国女主播采用分层管理的粉丝运营策略，包括实时弹幕互动、专属社群（如KakaoTalk开放聊天室、Discord）、粉丝等级体系、独家内容福利等。她们通过建立粉丝身份认同感和归属感，有效提升粉丝的忠诚度和消费意愿。",
  },
];

export default function Home() {
  const [email, setEmail] = useState("");
  const [subscribeStatus, setSubscribeStatus] = useState<"idle" | "success" | "error">("idle");

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.currentTarget as HTMLFormElement;
    const formData = new FormData(form);
    const honeypot = (formData.get("phone_alt") as string || "").trim();
    if (honeypot) {
      setSubscribeStatus("success");
      setEmail("");
      return;
    }
    try {
      const res = await fetch("https://readdy.ai/api/form/d9qr794nlsngrm4lm4hg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams({ email: formData.get("email") as string }).toString(),
      });
      const text = await res.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(text); } catch { /* ignore */ }
      const code = (parsed?.code as string) || "";
      if (res.ok && code === "OK") {
        setSubscribeStatus("success");
        setEmail("");
      } else {
        setSubscribeStatus("error");
      }
    } catch {
      setSubscribeStatus("error");
    }
  };

  return (
    <>
      <SEOHead
        title="韩国女主播直播风格与魅力解析 | K-Stream 解析"
        description="深度解析韩国女主播的直播风格、内容特色与粉丝运营技巧。了解韩国女主播为何能吸引全球观众，探索她们的直播平台选择、互动方式与变现模式，助你发现最值得关注的韩国女主播。"
        keywords="韩国女主播,韩国女主播直播平台,韩国女主播推荐,韩国女主播互动技巧,韩国女主播粉丝运营,韩国直播文化,韩国女主播变现方式"
        faqData={faqs}
        breadcrumbs={[{ name: "首页", url: window.location.origin + __BASE_PATH__ + "/" }]}
        ogType="website"
      />

      {/* Hero Section */}
      <section className="relative w-full min-h-[620px] flex items-center justify-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Korean%20female%20streamer%20in%20elegant%20modern%20studio%20with%20professional%20lighting%20setup%20warm%20ambient%20atmosphere%20sophisticated%20streaming%20environment%20with%20multiple%20screens%20and%20decorative%20elements%20editorial%20photography%20style%20soft%20focus%20background%20luxurious%20feel&width=1920&height=1080&seq=hero-kstream-2026&orientation=landscape"
          alt="韩国女主播直播风格解析"
          className="absolute inset-0 w-full h-full object-cover object-top"
          title="韩国女主播直播风格与魅力解析"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50"></div>
        <div className="relative z-10 w-full max-w-4xl mx-auto px-4 md:px-6 text-center py-24">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-background-50/15 backdrop-blur-sm border border-background-50/20 mb-6">
            <i className="ri-sparkling-line text-accent-400 text-sm"></i>
            <span className="text-background-50/90 text-sm font-medium">深度解析韩国直播文化</span>
          </div>
          <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-background-50 mb-6 leading-tight">
            韩国女主播
            <span className="block text-accent-300">直播风格与魅力解析</span>
          </h1>
          <p className="text-background-50/80 text-base md:text-lg max-w-2xl mx-auto mb-8 leading-relaxed">
            深度剖析韩国女主播的直播风格、互动技巧与粉丝运营策略。从平台选择到变现模式，
            从撒娇文化到跨文化传播，为你揭开韩国直播产业的核心密码。
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/category/streaming-style"
              className="w-full sm:w-auto px-8 py-3.5 rounded-full bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-colors no-underline whitespace-nowrap cursor-pointer"
            >
              探索直播风格
              <i className="ri-arrow-right-line ml-2"></i>
            </Link>
            <Link
              to="/category/streamer-recommendations"
              className="w-full sm:w-auto px-8 py-3.5 rounded-full bg-background-50/15 backdrop-blur-sm border border-background-50/30 text-background-50 font-semibold text-sm hover:bg-background-50/25 transition-colors no-underline whitespace-nowrap cursor-pointer"
            >
              查看主播推荐
            </Link>
          </div>
        </div>
      </section>

      {/* Category Navigation */}
      <section className="py-14 md:py-20 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
              探索分类
            </h2>
            <p className="text-foreground-600 text-sm md:text-base max-w-xl mx-auto">
              从多个维度深入了解韩国女主播的直播生态
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="group flex flex-col items-center gap-3 p-5 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-300 hover:bg-primary-50/30 transition-all duration-300 no-underline cursor-pointer"
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 group-hover:bg-primary-500 group-hover:text-background-50 transition-colors">
                  <i className={`${cat.icon} text-xl`}></i>
                </div>
                <div className="text-center">
                  <span className="block text-sm font-semibold text-foreground-950 whitespace-nowrap group-hover:text-primary-500 transition-colors">
                    {cat.name}
                  </span>
                  <span className="text-xs text-foreground-400 mt-0.5 block">
                    {cat.articleCount} 篇文章
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Articles */}
      <section className="py-14 md:py-20 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between mb-10">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-2">
                精选文章
              </h2>
              <p className="text-foreground-600 text-sm">
                编辑精选的最优质深度解析内容
              </p>
            </div>
            <Link
              to="/category/streaming-style"
              className="hidden sm:flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 font-medium no-underline whitespace-nowrap cursor-pointer"
            >
              查看全部
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {featuredArticles.map((article) => (
              <ArticleCard key={article.id} article={article} />
            ))}
          </div>
        </div>
      </section>

      {/* Latest Articles */}
      <section className="py-14 md:py-20 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center justify-between mb-10">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-2">
                最新文章
              </h2>
              <p className="text-foreground-600 text-sm">
                持续更新的韩国女主播深度解析内容
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {latestArticles.map((article) => (
              <ArticleCard key={article.id} article={article} />
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-12 bg-primary-500">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { num: "14+", label: "深度文章" },
              { num: "6", label: "内容分类" },
              { num: "100%", label: "原创内容" },
              { num: "全球", label: "覆盖范围" },
            ].map((stat) => (
              <div key={stat.label}>
                <p className="font-heading text-3xl md:text-4xl font-bold text-background-50 mb-1">
                  {stat.num}
                </p>
                <p className="text-background-50/70 text-sm">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-14 md:py-20 bg-background-50">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
              常见问题
            </h2>
            <p className="text-foreground-600 text-sm">
              关于韩国女主播，你最关心的问题都在这里
            </p>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, idx) => (
              <details
                key={idx}
                className="group border border-background-200/70 rounded-lg bg-background-50 overflow-hidden cursor-pointer"
              >
                <summary className="flex items-center justify-between p-4 md:p-5 text-sm md:text-base font-medium text-foreground-950 list-none">
                  {faq.question}
                  <i className="ri-add-line text-foreground-400 group-open:hidden"></i>
                  <i className="ri-subtract-line text-foreground-400 hidden group-open:block"></i>
                </summary>
                <div className="px-4 md:px-5 pb-4 md:pb-5 text-sm text-foreground-600 leading-relaxed">
                  {faq.answer}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Newsletter */}
      <section className="py-14 md:py-20 bg-background-950">
        <div className="max-w-2xl mx-auto px-4 md:px-6 text-center">
          <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-primary-500/20 mb-5">
            <i className="ri-mail-send-line text-2xl text-primary-400"></i>
          </div>
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-background-50 mb-3">
            获取最新韩国女主播解析
          </h2>
          <p className="text-background-50/60 text-sm mb-8 max-w-md mx-auto">
            订阅我们的内容更新，第一时间获取韩国女主播深度解析、热门推荐和行业趋势分析。
          </p>

          {subscribeStatus === "success" ? (
            <div className="flex items-center justify-center gap-2 text-accent-400">
              <i className="ri-checkbox-circle-line text-xl"></i>
              <span className="text-sm font-medium">订阅成功！感谢你的关注。</span>
            </div>
          ) : (
            <form
              onSubmit={handleSubscribe}
              data-readdy-form
              className="flex flex-col sm:flex-row items-center justify-center gap-3 max-w-md mx-auto"
            >
              <input
                type="email"
                name="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="输入你的邮箱地址"
                className="w-full sm:flex-1 px-5 py-3 rounded-full bg-background-50/10 border border-background-50/20 text-background-50 text-sm placeholder:text-background-50/40 focus:outline-none focus:border-primary-400 transition-colors"
              />
              <input
                type="text"
                name="phone_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="absolute opacity-0 pointer-events-none"
              />
              <button
                type="submit"
                className="w-full sm:w-auto px-8 py-3 rounded-full bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                立即订阅
              </button>
            </form>
          )}
          {subscribeStatus === "error" && (
            <p className="text-red-400 text-sm mt-3">订阅出现错误，请稍后重试。</p>
          )}
        </div>
      </section>
    </>
  );
}